/**
 * DocumentTypeSettings - Manage property definitions per document type
 * Accessible at /se/dt
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Trash2,
  GripVertical,
  Type,
  Hash,
  List,
  ListChecks,
  CheckSquare,
  Calendar,
  Link2,
  Mail,
  Phone,
  Loader2,
  X,
} from 'lucide-react';
import {
  propertiesApi,
  type PropertyDefinition,
  type PropertyType,
} from '../../api/properties';
import { useProject } from '../../contexts/ProjectContext';
import { useToast } from '../../contexts/ToastContext';

const DOC_TYPES = [
  { value: 'document', label: 'Document' },
  { value: 'spec', label: 'Specification' },
  { value: 'guide', label: 'Guide' },
  { value: 'note', label: 'Note' },
  { value: 'template', label: 'Template' },
] as const;

const PROPERTY_TYPE_OPTIONS: { value: PropertyType; label: string; icon: React.ReactNode }[] = [
  { value: 'rich_text', label: 'Text', icon: <Type size={14} /> },
  { value: 'number', label: 'Number', icon: <Hash size={14} /> },
  { value: 'select', label: 'Select', icon: <List size={14} /> },
  { value: 'multi_select', label: 'Multi Select', icon: <ListChecks size={14} /> },
  { value: 'checkbox', label: 'Checkbox', icon: <CheckSquare size={14} /> },
  { value: 'date', label: 'Date', icon: <Calendar size={14} /> },
  { value: 'url', label: 'URL', icon: <Link2 size={14} /> },
  { value: 'email', label: 'Email', icon: <Mail size={14} /> },
  { value: 'phone', label: 'Phone', icon: <Phone size={14} /> },
  { value: 'status', label: 'Status', icon: <List size={14} /> },
];

function getTypeIcon(type: PropertyType) {
  const found = PROPERTY_TYPE_OPTIONS.find(o => o.value === type);
  return found?.icon || <Type size={14} />;
}

export function DocumentTypeSettings() {
  const { projectId } = useProject();
  const toast = useToast();

  const [activeTab, setActiveTab] = useState<string>('document');
  const [definitions, setDefinitions] = useState<PropertyDefinition[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);

  // Add form state
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<PropertyType>('rich_text');
  const [newRequired, setNewRequired] = useState(false);

  const loadDefinitions = useCallback(async () => {
    if (!projectId) return;
    setIsLoading(true);
    try {
      const defs = await propertiesApi.listDefinitions(projectId, activeTab);
      setDefinitions(defs);
    } catch (err) {
      console.error('Failed to load property definitions:', err);
    } finally {
      setIsLoading(false);
    }
  }, [projectId, activeTab]);

  useEffect(() => {
    loadDefinitions();
  }, [loadDefinitions]);

  const handleCreateProperty = async () => {
    if (!projectId || !newName.trim()) return;
    try {
      await propertiesApi.createDefinition(projectId, {
        name: newName.trim(),
        property_type: newType,
        document_type: activeTab,
        is_required: newRequired,
      });
      toast.success(`Property "${newName}" created`);
      setNewName('');
      setNewType('rich_text');
      setNewRequired(false);
      setShowAddForm(false);
      loadDefinitions();
    } catch (err) {
      toast.error('Failed to create property');
    }
  };

  const handleDeleteProperty = async (def: PropertyDefinition) => {
    if (!projectId) return;
    if (!confirm(`Delete property "${def.name}"? This will remove all values.`)) return;
    try {
      await propertiesApi.deleteDefinition(projectId, def.id);
      toast.success(`Property "${def.name}" deleted`);
      loadDefinitions();
    } catch (err) {
      toast.error('Failed to delete property');
    }
  };

  const handleToggleRequired = async (def: PropertyDefinition) => {
    if (!projectId) return;
    try {
      await propertiesApi.updateDefinition(projectId, def.id, {
        is_required: !def.is_required,
      });
      loadDefinitions();
    } catch (err) {
      toast.error('Failed to update property');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-text">Document Types</h2>
        <p className="mt-1 text-sm text-muted">
          Configure custom properties for each document type
        </p>
      </div>

      {/* Document Type Tabs */}
      <div className="flex gap-1 border-b border-line">
        {DOC_TYPES.map(dt => (
          <button
            key={dt.value}
            onClick={() => setActiveTab(dt.value)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors
              ${activeTab === dt.value
                ? 'border-coral-500 text-coral-600'
                : 'border-transparent text-muted hover:text-text hover:border-line-strong'
              }`}
          >
            {dt.label}
          </button>
        ))}
      </div>

      {/* Properties Table */}
      <div className="bg-white border border-line rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={24} className="animate-spin text-subtle" />
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="grid grid-cols-[1fr_140px_80px_60px] gap-4 px-4 py-3 bg-surface-muted border-b border-line text-xs font-semibold text-muted uppercase tracking-wider">
              <span>Property</span>
              <span>Type</span>
              <span>Required</span>
              <span></span>
            </div>

            {/* Rows */}
            {definitions.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-subtle">
                No properties defined for this document type yet.
              </div>
            ) : (
              definitions.map(def => (
                <div
                  key={def.id}
                  className="grid grid-cols-[1fr_140px_80px_60px] gap-4 px-4 py-3 border-b border-line items-center hover:bg-surface-muted transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <GripVertical size={14} className="text-subtle cursor-grab" />
                    <span className="text-muted">{getTypeIcon(def.property_type as PropertyType)}</span>
                    <span className="text-sm font-medium text-text">{def.name}</span>
                  </div>
                  <span className="text-xs text-muted capitalize">
                    {def.property_type.replace('_', ' ')}
                  </span>
                  <button
                    onClick={() => handleToggleRequired(def)}
                    className={`w-10 h-5 rounded-full transition-colors ${
                      def.is_required ? 'bg-coral-500' : 'bg-surface-subtle'
                    }`}
                  >
                    <span className={`block w-4 h-4 rounded-full bg-white shadow-sm transform transition-transform ${
                      def.is_required ? 'translate-x-5' : 'translate-x-0.5'
                    }`} />
                  </button>
                  <button
                    onClick={() => handleDeleteProperty(def)}
                    className="p-1 text-subtle hover:text-red-500 transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))
            )}

            {/* Add Property */}
            {showAddForm ? (
              <div className="px-4 py-3 bg-surface-muted border-t border-line">
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    placeholder="Property name"
                    className="flex-1 px-3 py-2 text-sm border border-line-strong rounded-lg focus:ring-1 focus:ring-coral-500 focus:border-coral-500"
                    autoFocus
                    onKeyDown={e => e.key === 'Enter' && handleCreateProperty()}
                  />
                  <select
                    value={newType}
                    onChange={e => setNewType(e.target.value as PropertyType)}
                    className="px-3 py-2 text-sm border border-line-strong rounded-lg focus:ring-1 focus:ring-coral-500"
                  >
                    {PROPERTY_TYPE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <label className="flex items-center gap-1.5 text-xs text-muted">
                    <input
                      type="checkbox"
                      checked={newRequired}
                      onChange={e => setNewRequired(e.target.checked)}
                      className="rounded border-line-strong text-coral-500 focus:ring-coral-500"
                    />
                    Required
                  </label>
                  <button
                    onClick={handleCreateProperty}
                    disabled={!newName.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-coral-500 rounded-lg hover:bg-coral-600 disabled:opacity-50 transition-colors"
                  >
                    Add
                  </button>
                  <button
                    onClick={() => setShowAddForm(false)}
                    className="p-2 text-subtle hover:text-muted transition-colors"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowAddForm(true)}
                className="w-full px-4 py-3 flex items-center gap-2 text-sm text-muted hover:text-coral-600 hover:bg-surface-muted transition-colors"
              >
                <Plus size={16} />
                Add property
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default DocumentTypeSettings;
